/**
 * BrandLogo — the funkihoo icon-mark + optional typed wordmark.
 *
 * Three-asset brand model (lives in every consuming app's `public/`):
 *   - `funkihoo-icon.svg`     — the small, favicon-legible icon mark
 *                               with the brand-blue rounded-square
 *                               background. Consumed by the `icon` +
 *                               `wordmark` variants and by favicon /
 *                               apple-touch links in index.html.
 *   - `funkihoo-mark.svg`     — the bare mark (ring + crescent +
 *                               diamond) on a transparent background.
 *                               Consumed by the `mark` variant —
 *                               useful in icon-button chrome where
 *                               the heavy blue square would compete
 *                               with the surrounding affordances.
 *   - `funkihoo-wordmark.svg` — the rich bilingual lockup. NOT
 *                               consumed here — referenced directly
 *                               via `<img src>` from the splash +
 *                               og:image surfaces where its detail
 *                               survives.
 *
 * Why split: the bilingual wordmark is ~40 paths and unreadable
 * below ~120 px. A single asset can't serve from 16 px (favicon) to
 * 1200 px (og:image) — different surfaces need different marks.
 *
 * Variants:
 *   - `icon` — icon mark with the rounded blue background. Use in
 *     tight headers, mobile chrome, icon-button-style triggers.
 *   - `mark` — bare ring + crescent on transparent. Use inside icon-
 *     button chrome (the in-app header tab strip, etc.) where the
 *     rounded blue square would feel heavier than the surrounding
 *     icons.
 *   - `wordmark` — icon mark + the word "funkihoo" beside it in the
 *     display font (NOT the bilingual SVG — that's a separate file
 *     above). Use in shell headers + footers where the visitor
 *     benefits from seeing the brand name spelled out.
 *
 * Sizes follow the typical UI scale (16/24/32/40/56). Pass `size`
 * as a number for arbitrary pixel widths.
 *
 * The component renders an `<img>` (not inlined SVG) — keeps the JS
 * bundle small and lets the browser cache the asset across pages.
 */

import { cn } from '../lib/cn.js';

export type BrandLogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
export type BrandLogoVariant = 'icon' | 'mark' | 'wordmark';

const SIZE_PX: Record<Exclude<BrandLogoSize, number>, number> = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 56,
};

const WORDMARK_TEXT_SIZE: Record<Exclude<BrandLogoSize, number>, string> = {
  xs: 'text-sm',
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-xl',
  xl: 'text-2xl',
};

export interface BrandLogoProps {
  /** Layout: icon (rounded blue square), mark (bare ring + crescent),
   *  or icon + wordmark text. Defaults to `wordmark`. */
  variant?: BrandLogoVariant;
  /** Size of the icon mark. Numeric values are interpreted as pixels. */
  size?: BrandLogoSize;
  /** Optional className passed to the outer wrapper. */
  className?: string;
  /**
   * Path to the SVG asset. Defaults depend on `variant`:
   *   - `icon` / `wordmark` → `/funkihoo-icon.svg` (compact rounded
   *     blue square mark)
   *   - `mark` → `/funkihoo-mark.svg` (bare ring + crescent + diamond
   *     on a transparent background)
   *
   * Override when an app's public/ folder uses a different path.
   *
   * The rich bilingual asset lives at `/funkihoo-wordmark.svg` and is
   * NOT consumed by this component — it's referenced directly via
   * `<img src>` from the splash + og:image surfaces where its detail
   * survives. See `docs/architecture/deployment.md` (or the brand
   * commit) for the three-asset rationale.
   */
  src?: string;
  /**
   * Accessible label. Defaults to "funkihoo" so the brand name is
   * announced regardless of variant.
   */
  alt?: string;
}

function resolveSizePx(size: BrandLogoSize): number {
  return typeof size === 'number' ? size : SIZE_PX[size];
}

function resolveWordmarkText(size: BrandLogoSize): string {
  if (typeof size === 'number') {
    // Map common pixel sizes to the nearest scale step so arbitrary
    // numeric inputs still render a sensibly-sized wordmark.
    if (size <= 22) return WORDMARK_TEXT_SIZE.xs;
    if (size <= 28) return WORDMARK_TEXT_SIZE.sm;
    if (size <= 36) return WORDMARK_TEXT_SIZE.md;
    if (size <= 48) return WORDMARK_TEXT_SIZE.lg;
    return WORDMARK_TEXT_SIZE.xl;
  }
  return WORDMARK_TEXT_SIZE[size];
}

export function BrandLogo({
  variant = 'wordmark',
  size = 'md',
  className,
  src,
  alt = 'funkihoo',
}: BrandLogoProps) {
  const px = resolveSizePx(size);
  // Default asset depends on variant: `mark` uses the bare ring +
  // crescent on transparent; `icon` / `wordmark` use the rounded
  // blue square. `src` can override either.
  const resolvedSrc = src ?? (variant === 'mark' ? '/funkihoo-mark.svg' : '/funkihoo-icon.svg');
  const isStandalone = variant !== 'wordmark';
  const img = (
    <img
      src={resolvedSrc}
      alt={isStandalone ? alt : ''}
      width={px}
      height={px}
      // The rounded chrome is only applied to the blue-square `icon`
      // asset — it softens the corners so the app-icon mark sits well
      // in headers and tap targets. The bare `mark` asset has no
      // background, so adding a radius would just clip its edges and
      // produce a faint asymmetric crop on devices that anti-alias
      // the corners differently.
      className={cn('shrink-0 select-none', variant === 'icon' ? 'rounded-[20%]' : null)}
      draggable={false}
    />
  );

  if (isStandalone) {
    return <span className={cn('inline-flex', className)}>{img}</span>;
  }

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      {img}
      <span
        className={cn(
          'font-display font-semibold text-primary-700 leading-none',
          resolveWordmarkText(size),
        )}
      >
        funkihoo
      </span>
    </span>
  );
}
